<template>
  <div
    :class="wrapperClasses"
    data-auto-testid="app-multiple-radio-group"
    data-testid="app-multiple-radio-group"
    role="group"
  >
    <div class="amrg-Options">
      <div
        v-for="option in options"
        :key="option.value"
        :class="{ 'amrg-Item-disabled': option.disabled }"
        class="amrg-Item"
      >
        <input
          :id="`amrg-input-${instanceUid}-${option.value}`"
          v-model="selectedValues"
          :data-auto-testid="`amrg-input-${instanceUid}-${option.value}`"
          :data-testid="`amrg-input-${instanceUid}-${option.value}`"
          :disabled="option.disabled || disabled"
          :name="name"
          :value="option.value"
          class="amrg-OptionInput"
          hidden
          type="checkbox"
        />
        <label
          :id="`amrg-option-${instanceUid}-${option.value}`"
          :for="`amrg-input-${instanceUid}-${option.value}`"
          class="amrg-Option"
        >
          <AppTippy
            v-if="option.tooltip || option.tooltipContent"
            :arrow="true"
            :offset="[0, 14]"
            :theme="DROP_LIST_THEMES.TRANSLUCENT_NEXT"
            :to="`#amrg-option-${instanceUid}-${option.value}`"
            placement="top"
          >
            <template #content>
              <i18n-t
                v-if="option.tooltip"
                :keypath="option.tooltip"
                class="amrg-Option_TooltipContent"
                scope="global"
                tag="span"
              >
                <template #break><br /></template>
              </i18n-t>

              <span v-else-if="option.tooltipContent" class="amrg-Option_TooltipContent">
                {{ option.tooltipContent }}
              </span>
            </template>
          </AppTippy>

          <slot :item="option" name="item-label">
            <slot :item="option" name="before-label">
              <AppIcon
                v-if="option.icon"
                :icon-name="option.icon"
                class="amrg-Option_Icon"
                height="24"
                width="24"
              />
            </slot>

            <span class="amrg-Option_Label oboard-truncated-text">{{ option.label }}</span>
          </slot>
        </label>
        <slot :item="option" name="item-after" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { isEmpty, isEqual } from 'lodash'
import { computed, watch } from 'vue'

import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { MULTIPLE_RADIO_GROUP_TYPES } from '@/utils/components-configurations/app-radio-group'
import { includesValue } from '@/utils/prop-validators'
import { uid } from '@/utils/uid'

import AppIcon from '@/components/ui/AppIcon/AppIcon'

defineOptions({
  name: 'AppMultipleRadioGroup'
})

const props = defineProps({
  options: {
    type: Array,
    required: true
  },

  name: {
    type: String,
    required: true
  },

  disabled: {
    type: Boolean,
    default: false
  },

  variant: {
    type: String,
    default: MULTIPLE_RADIO_GROUP_TYPES.HORIZONTAL,
    validator: v => includesValue(v, MULTIPLE_RADIO_GROUP_TYPES)
  },

  isError: {
    type: Boolean
  },

  givenValuesOnReset: {
    type: Array,
    default: () => []
  },

  resetOnLastUnselect: {
    type: Boolean
  }
})

const model = defineModel({
  type: Array,
  required: true
})

const instanceUid = uid()

const wrapperClasses = computed(() => ({
  'amrg-AppMultipleRadioGroup': true,
  [`amrg-AppMultipleRadioGroup-${props.variant}`]: true,
  'amrg-AppMultipleRadioGroup-disabled': props.disabled,
  'amrg-AppMultipleRadioGroup-error': props.isError
}))

const getResetSelection = (values = props.givenValuesOnReset) => {
  if (isEmpty(values)) {
    return []
  }

  return values.filter(value => props.options.some(option => option.value === value))
}

const selectedValues = computed({
  get() {
    return model.value || []
  },

  set(values) {
    model.value = getResetSelection(
      props.resetOnLastUnselect && isEmpty(values) ? props.givenValuesOnReset : values
    )
  }
})

watch(
  model,
  (newValue, oldValue) => {
    if (isEqual(newValue, oldValue)) return

    if (!props.resetOnLastUnselect) return

    if (!isEmpty(model.value)) return

    const next = getResetSelection()

    if (isEmpty(next)) return

    model.value = next
  },
  { deep: true }
)
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';

.amrg-AppMultipleRadioGroup {
  display: flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  min-width: 24px;

  &-disabled {
    pointer-events: none;
    opacity: 0.5;
    cursor: not-allowed;
  }

  &-horizontal {
    padding: 4px;
    border-radius: $border-radius-sm-next;
    background-color: $grey-19;
  }

  &-error#{&}-horizontal {
    box-shadow: 0 0 0 1px $grade-low-color-next;
  }
}

.amrg-Options {
  align-items: center;
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.amrg-Item {
  flex: 0 1 auto;
  min-width: 0;
  user-select: none;

  &-disabled {
    opacity: 0.5;
    pointer-events: none;

    .amrg-Option {
      cursor: not-allowed;
    }
  }
}

.amrg-Option {
  color: $dark-grey;
  padding: var(--ob-amrg-Option-padding, 0 6px);
  border: none;
  font-family: $system-ui;
  font-size: $fs-12;
  font-weight: fw('bold');
  background-color: transparent;
  border-radius: $border-radius-sm;
  display: flex;
  align-items: center;
  gap: var(--ob-amrg-Option-gap, 4px);
  cursor: pointer;
  transition: all $transition-fast ease;
  line-height: 16px;
  min-height: 24px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  white-space: nowrap;

  &:hover {
    background-color: rgba($white, 0.8);
  }
}

.amrg-Option_Icon {
  flex-shrink: 0;
}

.amrg-Option_Label {
  min-width: 0;
  overflow: hidden;
}

.amrg-OptionInput {
  &:checked + .amrg-Option {
    background-color: $white;
    color: $dark-grey;
    box-shadow: 0 0 1px rgba($black, 0.15), 0 2px 4px rgba($black, 0.15);
  }
}

.amrg-Option_TooltipContent {
  text-align: center;
  display: inline-block;
}
</style>
